/**
 * ScrollManager - Gestion avancée du scroll et optimisation performance
 */

import { $$, on, debounce, throttle } from "../utils/dom.js";

export class ScrollManager {
  constructor() {
    this.scrollListeners = new Map();
    this.isScrolling = false;
    this.scrollDirection = "down";
    this.lastScrollTop = 0;
    this.scrollTop = 0;

    this.init();
  }

  async init() {
    this.setupScrollTracking();
    this.setupSmoothScroll();
  }

  /**
   * Configuration du tracking de scroll optimisé
   */
  setupScrollTracking() {
    const updateScroll = throttle(() => {
      this.scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      this.scrollDirection =
        this.scrollTop > this.lastScrollTop ? "down" : "up";

      // Mettre à jour les CSS custom properties pour usage en CSS
      document.documentElement.style.setProperty(
        "--scroll-y",
        `${this.scrollTop}px`,
      );
      document.documentElement.style.setProperty(
        "--scroll-direction",
        this.scrollDirection === "down" ? "1" : "-1",
      );

      this.lastScrollTop = this.scrollTop;

      // Notifier les listeners
      this.notifyScrollListeners();
    }, 16); // ~60fps

    // Détecter début/fin de scroll
    let scrollTimer;
    const handleScroll = () => {
      if (!this.isScrolling) {
        this.isScrolling = true;
        document.documentElement.classList.add("is-scrolling");
      }

      updateScroll();

      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        this.isScrolling = false;
        document.documentElement.classList.remove("is-scrolling");
      }, 150);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
  }

  /**
   * Scroll fluide natif avec fallback
   */
  setupSmoothScroll() {
    // Liens d'ancrage avec scroll fluide
    on(document, "click", 'a[href^="#"]', (e) => {
      const href = e.currentTarget.getAttribute("href");
      if (href === "#") return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        this.scrollToElement(target);
      }
    });
  }

  /**
   * Ajouter un listener de scroll optimisé
   */
  addScrollListener(id, callback, options = {}) {
    const config = {
      throttle: true,
      throttleMs: 16,
      passive: true,
      ...options,
    };

    let finalCallback = callback;

    if (config.throttle) {
      finalCallback = throttle(callback, config.throttleMs);
    }

    this.scrollListeners.set(id, {
      callback: finalCallback,
      config,
    });
  }

  /**
   * Supprimer un listener de scroll
   */
  removeScrollListener(id) {
    this.scrollListeners.delete(id);
  }

  /**
   * Notifier tous les listeners
   */
  notifyScrollListeners() {
    this.scrollListeners.forEach(({ callback }) => {
      callback({
        scrollTop: this.scrollTop,
        direction: this.scrollDirection,
        isScrolling: this.isScrolling,
      });
    });
  }

  /**
   * Scroll fluide vers un élément
   */
  scrollToElement(element, options = {}) {
    const config = {
      behavior: "smooth",
      block: "start",
      inline: "nearest",
      offset: 0,
      ...options,
    };

    const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementTop + config.offset;

    // Utiliser scrollIntoView si supporté et pas d'offset
    if ("scrollIntoView" in element && config.offset === 0) {
      element.scrollIntoView({
        behavior: config.behavior,
        block: config.block,
        inline: config.inline,
      });
    } else {
      // Fallback avec scroll manuel
      window.scrollTo({
        top: offsetPosition,
        behavior: config.behavior,
      });
    }

    return new Promise((resolve) => {
      // Attendre la fin du scroll
      const checkEnd = () => {
        if (!this.isScrolling) {
          resolve();
        } else {
          requestAnimationFrame(checkEnd);
        }
      };
      setTimeout(checkEnd, 100);
    });
  }

  /**
   * Scroll vers le haut de la page
   */
  scrollToTop(smooth = true) {
    window.scrollTo({
      top: 0,
      behavior: smooth ? "smooth" : "auto",
    });
  }

  /**
   * Obtenir la position de scroll
   */
  getScrollPosition() {
    return {
      x: window.pageXOffset || document.documentElement.scrollLeft,
      y: window.pageYOffset || document.documentElement.scrollTop,
    };
  }

  /**
   * Obtenir les dimensions de la page
   */
  getPageDimensions() {
    return {
      width: Math.max(
        document.body.scrollWidth,
        document.body.offsetWidth,
        document.documentElement.clientWidth,
        document.documentElement.scrollWidth,
        document.documentElement.offsetWidth,
      ),
      height: Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight,
      ),
      viewportWidth: window.innerWidth || document.documentElement.clientWidth,
      viewportHeight:
        window.innerHeight || document.documentElement.clientHeight,
    };
  }

  /**
   * Calculer le pourcentage de scroll
   */
  getScrollPercentage() {
    const { height, viewportHeight } = this.getPageDimensions();
    const scrollableHeight = height - viewportHeight;
    return scrollableHeight > 0 ? (this.scrollTop / scrollableHeight) * 100 : 0;
  }

  /**
   * Vérifier si un élément est visible dans le viewport
   */
  isElementVisible(element, threshold = 0) {
    const rect = element.getBoundingClientRect();
    const { viewportHeight } = this.getPageDimensions();

    return rect.top <= viewportHeight - threshold && rect.bottom >= threshold;
  }

  /**
   * Attacher au contexte Drupal
   */
  attach(context) {
    // Réinitialiser le smooth scroll pour le nouveau contenu
    this.setupSmoothScroll();
  }
}

/**
 * ScrollIndicator - Indicateur de progression du scroll
 */
export class ScrollIndicator {
  constructor(element = null) {
    this.element = element || this.createIndicator();
    this.scrollManager = null;
    this.init();
  }

  createIndicator() {
    const indicator = document.createElement("div");
    indicator.className = "scroll-indicator";
    indicator.innerHTML = '<div class="scroll-indicator__bar"></div>';

    Object.assign(indicator.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100%",
      height: "3px",
      background: "rgba(0, 0, 0, 0.1)",
      zIndex: "9999",
      pointerEvents: "none",
    });

    const bar = indicator.querySelector(".scroll-indicator__bar");
    Object.assign(bar.style, {
      height: "100%",
      background: "var(--color-primary-base, #007acc)",
      width: "0%",
      transition: "width 0.1s ease-out",
    });

    document.body.appendChild(indicator);
    return indicator;
  }

  init() {
    // Utiliser le ScrollManager global si disponible
    if (window.scrollManager) {
      this.scrollManager = window.scrollManager;
      this.scrollManager.addScrollListener("scroll-indicator", (data) => {
        this.updateProgress();
      });
    }
  }

  updateProgress() {
    if (!this.scrollManager) return;

    const percentage = this.scrollManager.getScrollPercentage();
    const bar = this.element.querySelector(".scroll-indicator__bar");

    if (bar) {
      bar.style.width = `${percentage}%`;
    }
  }

  show() {
    this.element.style.display = "block";
  }

  hide() {
    this.element.style.display = "none";
  }
}
